/**
 * Utility functions for formatting values in the application
 */

import { readCachedSystemConfig } from "../shared/lib/systemConfig";

/**
 * Convert API numeric-like value into a safe number.
 * Accepts numbers and formatted strings like "100,000.00".
 * @param {number|string|null|undefined} value
 * @param {number} fallback
 * @returns {number}
 */
export const toApiNumber = (value, fallback = 0) => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : fallback;
    }
    if (typeof value === "string") {
        const normalized = value
            .trim()
            .replace(/,/g, "")
            .replace(/[^\d.-]/g, "");
        if (!normalized) return fallback;
        const parsed = Number.parseFloat(normalized);
        return Number.isFinite(parsed) ? parsed : fallback;
    }
    return fallback;
};

const getSystemNumberOptions = () => {
    const cfg = readCachedSystemConfig();
    return {
        locale: cfg?.locale || "en-US",
        currency_code: cfg?.currency_code || "IQD",
        currency_display: cfg?.currency_display || "code",
        fraction_digits: Number.isFinite(cfg?.fraction_digits) ? cfg.fraction_digits : (cfg?.currency_code === "IQD" ? 0 : 2),
    };
};

/**
 * Format a number as a locale string with thousands separators
 * @param {number} number - The number to format
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @returns {string} - The formatted number as a string
 */
export const formatNumber = (number, locale) => {
    const parsed = toApiNumber(number, Number.NaN);
    if (!Number.isFinite(parsed)) {
        return '-';
    }
    const sys = getSystemNumberOptions();
    const usedLocale = locale || sys.locale;
    return parsed.toLocaleString(usedLocale);
};

/**
 * Format a currency value with the specified currency code
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - The currency code (e.g., 'IQD')
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @returns {string} - Formatted currency string (e.g., 'IQD 1,000')
 */
export const formatCurrency = (amount, currencyCode, locale) => {
    let parsed = toApiNumber(amount, Number.NaN);
    if (!Number.isFinite(parsed)) {
        return '-';
    }
    const sys = getSystemNumberOptions();
    const usedLocale = locale || sys.locale;
    const usedCurrency =
        currencyCode !== undefined && currencyCode !== null && String(currencyCode).trim() !== ""
            ? String(currencyCode).trim()
            : (sys.currency_code || "IQD");
    const fractionDigits = Number.isFinite(sys.fraction_digits) ? sys.fraction_digits : (usedCurrency === "IQD" ? 0 : 2);

    // Round to configured decimals and drop -0 / sub-cent drift so lists never show "-USD 0".
    const factor = 10 ** fractionDigits;
    parsed = Math.round(parsed * factor) / factor + 0; /* +0 flips IEEE -0 → 0 */

    try {
        return new Intl.NumberFormat(usedLocale, {
            style: "currency",
            currency: usedCurrency,
            currencyDisplay: sys.currency_display || "code",
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        }).format(parsed);
    } catch (e) {
        // Fallback if Intl fails for any reason
        return `${usedCurrency} ${parsed.toLocaleString(usedLocale, {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        })}`;
    }
};
